import React, { useMemo, useState } from "react";

import { Box, Typography, Button, Menu, MenuItem } from "@mui/material";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import DashboardTabFilter from "./reuseable-components/DashboardTabFilter";
import DashBoardTable from "./reuseable-components/DashBoardTable";
import {
  downloadCSV,
  downloadExcel,
  downloadPDF,
} from "../../utils/exportUtils";

const formatVolunteerDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString("en-CA");
};

const VolunteersTab = ({ volunteers = [] }) => {
  const columns = [
    { id: "name", label: "Name", sortable: true },
    { id: "email", label: "Email", sortable: true },
    { id: "phone", label: "Phone", sortable: true },
    { id: "zip_code", label: "Zip Code", sortable: true },
    { id: "interests", label: "Interests", sortable: false },
    { id: "status", label: "Status", sortable: true },
    { id: "date", label: "Date", sortable: true },
  ];

  /* =========================================
      ORIGINAL DATA
  ========================================= */

  const volunteerRows = useMemo(
    () =>
      volunteers.map((volunteer) => ({
        name:
          [volunteer.firstName, volunteer.lastName].filter(Boolean).join(" ") ||
          "-",
        email: volunteer.email || "-",
        phone: volunteer.phone || "-",
        interests:
          Array.isArray(volunteer.interests) && volunteer.interests.length > 0
            ? volunteer.interests.join(", ")
            : "-",
        zip_code: volunteer.zipCode || "-",
        status: volunteer.status || "-",
        date: formatVolunteerDate(volunteer.createdAt),
      })),
    [volunteers],
  );

  /* =========================================
      STATES
  ========================================= */

  const defaultFilters = {
    search: "",
    zip_code: "",
    source: "All Interests",
    from_date: "",
    to_date: "",
  };

  const interestOptions = [
    { label: "All Interests", value: "All Interests" },
    { label: "Canvassing", value: "Canvassing" },
    { label: "Phone Banking", value: "Phone Banking" },
    { label: "Yard Sign", value: "Yard Sign" },
    { label: "Events", value: "Events" },
    { label: "Social Media", value: "Social Media" },
    { label: "Petition", value: "Petition" },
  ];

  const [filters, setFilters] = useState(defaultFilters);

  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);

  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [page, setPage] = useState(1);

  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  /* =========================================
      FILTER CHANGE
  ========================================= */

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setPage(1);
  };

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  /* =========================================
      SORTING
  ========================================= */

  const handleSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({
      key,
      direction,
    });
  };

  /* =========================================
      FILTER + SORT DATA
  ========================================= */

  const filteredRows = useMemo(() => {
    let data = [...volunteerRows];

    // SEARCH
    if (appliedFilters.search) {
      const search = appliedFilters.search.toLowerCase();

      data = data.filter(
        (item) =>
          item.name.toLowerCase().includes(search) ||
          item.email.toLowerCase().includes(search) ||
          item.phone.includes(search),
      );
    }

    // ZIP CODE
    if (appliedFilters.zip_code) {
      data = data.filter((item) =>
        item.zip_code.includes(appliedFilters.zip_code),
      );
    }

    // INTERESTS
    if (appliedFilters.source !== "All Interests") {
      data = data.filter((item) =>
        item.interests.includes(appliedFilters.source),
      );
    }

    // FROM DATE
    if (appliedFilters.from_date) {
      data = data.filter(
        (item) => new Date(item.date) >= new Date(appliedFilters.from_date),
      );
    }

    // TO DATE
    if (appliedFilters.to_date) {
      data = data.filter(
        (item) => new Date(item.date) <= new Date(appliedFilters.to_date),
      );
    }

    // SORTING
    if (sortConfig.key) {
      data.sort((a, b) => {
        const valueA = a[sortConfig.key];

        const valueB = b[sortConfig.key];

        if (valueA < valueB) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }

        if (valueA > valueB) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }

        return 0;
      });
    }

    return data;
  }, [appliedFilters, sortConfig, volunteerRows]);

  /* =========================================
      PAGINATION
  ========================================= */

  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;

    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        p: {
          xs: 1,
          md: 3,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#333",
          }}
        >
          Volunteer Applications
        </Typography>

        <>
          <Button
            variant="contained"
            endIcon={<ArrowDropDownIcon />}
            onClick={handleExportClick}
            sx={{
              bgcolor: "#D4A017",
              textTransform: "none",
              "&:hover": {
                bgcolor: "#B8860B",
              },
            }}
          >
            Export
          </Button>

          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            <MenuItem
              onClick={() => {
                downloadCSV({
                  data: filteredRows,
                  fileName: "volunteer-applications",
                });
                handleClose();
              }}
            >
              Download CSV
            </MenuItem>

            <MenuItem
              onClick={() => {
                downloadExcel({
                  data: filteredRows,
                  fileName: "volunteer-applications",
                  sheetName: "Volunteer Applications",
                });
                handleClose();
              }}
            >
              Download Excel
            </MenuItem>

            <MenuItem
              onClick={() => {
                downloadPDF({
                  data: filteredRows,
                  fileName: "volunteer-applications",
                  title: "Volunteer Applications",
                  columns,
                });
                handleClose();
              }}
            >
              Download PDF
            </MenuItem>
          </Menu>
        </>
      </Box>

      {/* Filters */}
      <DashboardTabFilter
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        sourceLabel="Interests"
        sourceOptions={interestOptions}
      />

      {/* Table */}
      <DashBoardTable
        columns={columns}
        rows={paginatedRows}
        sortConfig={sortConfig}
        onSort={handleSort}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(value) => {
          setRowsPerPage(value);
          setPage(1);
        }}
        totalResults={filteredRows.length}
      />
    </Box>
  );
};

export default VolunteersTab;
